/**
 * [TD-89 v2 Capability B + HF-TD89] Inline Abandon Draft button for the
 * DraftRoom commissioner-controls slot. Renders for commissioner when
 * status is DRAFTING or PAUSED (HF-TD89 expansion - commissioners often
 * pause to think, then decide to abandon). Pause + Abandon coexist per
 * Guard 2 - this sits alongside the existing Pause/Resume/Skip Nominator
 * surfaces, not replacing them.
 */
import { useState } from "react";
import { OctagonX } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { authHeaders } from "../../contexts/AuthContext";
import ConfirmModal from "../common/ConfirmModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DraftRoomAbandonButton({ league, isCommissioner, isDark = true }) {
  const [confirm, setConfirm] = useState(false);
  const nav = useNavigate();
  if (!isCommissioner) return null;
  if (!["DRAFTING", "PAUSED"].includes(league?.status)) return null;

  const handleAbandon = async () => {
    try {
      await axios.post(
        `${API}/commissioner/${league.id}/abandon-draft`,
        {},
        { headers: authHeaders() },
      );
      toast.success("Draft abandoned. Returning to lobby...");
      nav(`/league/${league.id}/lobby`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Abandon failed");
    }
  };

  return (
    <>
      <button
        data-testid="draft-room-abandon-btn"
        onClick={() => setConfirm(true)}
        title="Abandon draft (wipes progress, returns to lobby)"
        className="px-3 flex items-center gap-1.5 text-[#E03030] hover:bg-[#E03030]/[0.10] border-l border-white/[0.06] transition-colors"
      >
        <OctagonX size={14} />
        <span className="font-display font-bold text-xs uppercase tracking-wider hidden lg:inline">Abandon</span>
      </button>
      <ConfirmModal
        open={confirm}
        onCancel={() => setConfirm(false)}
        onConfirm={async () => { setConfirm(false); await handleAbandon(); }}
        title="Abandon Draft?"
        body={
          <>
            All draft progress will be lost. Managers stay seated. You can
            restart the draft from the lobby when ready.
          </>
        }
        confirmLabel="Abandon Draft"
        destructive
        isDark={isDark}
        testIdPrefix="draft-room-confirm-abandon"
      />
    </>
  );
}
